import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Wraps the /api/funnel/* endpoints. The session_token (JWT) is stored in
 * sessionStorage so a page reload during the quiz doesn't lose state.
 *
 * Backend contract: see backend/app/controllers/api/funnel/{leads,quiz,checkout}_controller.rb
 */

export type LeadStatus = 'email_sent' | 'redirect_login';

export interface LeadResponse {
  status:           LeadStatus;
  message?:         string;
  login_url?:       string;
  /** true when the email matches a user who already owns a FAC3LESS card */
  existing_account?: boolean;
}

export interface QuizOption {
  value:    string;
  i18n_key: string;
}

export interface QuizQuestion {
  key:       string;
  i18n_key:  string;
  /** Option-based questions carry an option list; free-text questions omit it and set input='text'. */
  options?:  QuizOption[];
  /** 'text' for a free-text (textarea) question; undefined for single-select. */
  input?:    'text';
  /** Minimum character count for free-text questions. */
  min?:      number;
  progress:  { current: number; total: number };
}

export interface QuizProduct {
  id:          number;
  code:        string;
  name:        string;
  price:       number | string;
  /**
   * Set only for a product whose `price` is not the whole price the customer is
   * quoted — today the fac3less card, whose activation fee is paid directly to the
   * card partner in the card dashboard and never charged here.
   */
  activation_fee?:       number | string | null;
  /** `price` + `activation_fee`; equals `price` when there is no fee. */
  total_customer_price?: number | string;
  description: string;
}

export type QuizStatus = 'started' | 'completed' | 'purchased' | 'abandoned';

export interface QuizSession {
  session_id:                 number;
  status:                     QuizStatus;
  answers:                    Record<string, string>;
  session_token?:             string;
  next_question:              QuizQuestion | null;
  recommended_product_code?:  string;
  product?:                   QuizProduct;
}

export interface CheckoutResponse {
  order_id:         number;
  status:           string;
  payment_method:   string;
  total_price:      number | string;
  total_with_costs: number | string;
  product:          { code: string; name: string };
}

export interface PasswordSetupResponse {
  status: string;
  /** Address the password-setup link was emailed to (the funnel user's own). */
  email?: string;
}

const SESSION_TOKEN_KEY = 'fac3less.funnel.session_token';

@Injectable({ providedIn: 'root' })
export class FunnelService {
  private http = inject(HttpClient);

  // ----- Leads ------------------------------------------------------------

  createLead(input: {
    first_name:     string;
    last_name?:     string;
    email:          string;
    phone?:         string;
    country?:       string;
    sponsor_id?:    string | number | null;
    language?:      string;
    captcha_token?: string | null;
  }): Observable<LeadResponse> {
    return this.http.post<LeadResponse>('/funnel/leads', input);
  }

  // ----- Quiz -------------------------------------------------------------

  startQuiz(magicToken: string): Observable<QuizSession> {
    return this.http.post<QuizSession>('/funnel/quiz/start', { token: magicToken });
  }

  showQuiz(): Observable<QuizSession> {
    return this.http.get<QuizSession>('/funnel/quiz', { headers: this.sessionHeaders() });
  }

  answerQuiz(key: string, value: string): Observable<QuizSession> {
    return this.http.post<QuizSession>(
      '/funnel/quiz/answer',
      { key, value },
      { headers: this.sessionHeaders() }
    );
  }

  /**
   * Emails the soft-logged-in funnel user a password-setup / login link so they
   * can create their account without buying first (or to finish later if a
   * purchase failed). The backend resolves the user from the funnel session.
   */
  requestPasswordSetup(): Observable<PasswordSetupResponse> {
    return this.http.post<PasswordSetupResponse>(
      '/funnel/quiz/password_setup',
      {},
      { headers: this.sessionHeaders() }
    );
  }

  /**
   * WolfPack wizard ending: set the password inline for the soft-logged-in
   * funnel user (no purchase, no second email). Resolves the user from the
   * funnel session on the backend.
   */
  setPassword(password: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      '/funnel/quiz/set_password',
      { password },
      { headers: this.sessionHeaders() }
    );
  }

  // ----- Checkout ---------------------------------------------------------

  createCheckout(paymentMethod: string): Observable<CheckoutResponse> {
    return this.http.post<CheckoutResponse>(
      '/funnel/quiz/checkout',
      { payment_method: paymentMethod },
      { headers: this.sessionHeaders() }
    );
  }

  // ----- Session token ----------------------------------------------------

  storeSessionToken(token: string): void {
    try { sessionStorage.setItem(SESSION_TOKEN_KEY, token); } catch { /* ignore */ }
  }

  getSessionToken(): string | null {
    try { return sessionStorage.getItem(SESSION_TOKEN_KEY); } catch { return null; }
  }

  clearSessionToken(): void {
    try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
  }

  private sessionHeaders(): HttpHeaders {
    const token = this.getSessionToken() ?? '';
    return new HttpHeaders({ 'X-Funnel-Session': token });
  }
}
